import { PanelBlotterTableStorageProvider } from '@src/components/AppLayout/Panels/Trading/blotters/PanelBlotterTableStorageProvider';
import { PanelTabsStorageProvider } from '@src/components/AppLayout/Panels/Trading/blotters/PanelTabsStorageProvider';
import { useDisplaySettings } from '@src/providers/AppConfigProvider';
import { useMobileLayoutPanelApi } from '@src/providers/MobileLayoutPanelProvider';
import {
  MixpanelEvent,
  MixpanelEventProperty,
  SubTabBar,
  useCallbackRef,
  useMixpanel,
  type IconName,
  type PersistedDefaultTabProps,
  type SubTabBarAddOption,
  type TabBarItem,
  type TabProps,
} from '@talos/kyoko';
import type { GroupviewPanelState } from 'dockview';
import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { v1 as uuid } from 'uuid';
import type { NavigationItem } from '../../../../tokens/navigationPaths';
import type { LayoutSection, LayoutSpec } from '../../../../types/LayoutConfig';
import { getMobileLayoutPanelRoute, getMobileLayoutPanelTabRoute } from '../../mobileLayoutRoutes';
import {
  getBlotterTablesStateFromPanelConfig,
  getTabsStateFromPanelConfig,
  type PanelConfigWithTabs,
} from './navRoutes';
import { usePanelTabNavItems } from './usePanelTabNavItems';
import { useResolvedTab } from './useResolvedTab';

export interface MobileBlotterPanelRenderTabArgs<T extends TabProps> {
  /** The resolved tab config (persisted or default). */
  tab: T | PersistedDefaultTabProps<T>;
  /** Tab ID from the URL. */
  tabId: string;
  /** Panel config — useful for blotterID composition or extra state lookup. */
  panelConfig: PanelConfigWithTabs<T> | undefined;
  /** Panel ID — used as the prefix for blotterID. */
  panelId: string;
  /** Persist an updated version of the active tab into panel params. */
  updateTab: (updatedTab: T | PersistedDefaultTabProps<T>) => void;
}

export interface MobileBlotterPanelAddTabOption<T extends TabProps> {
  id: string;
  label: string;
  icon?: IconName;
  createTab: (sourceTabs: readonly (T | PersistedDefaultTabProps<T>)[]) => Partial<T>;
  onAdd?: () => void;
}

interface MobileBlotterPanelTabsState<T extends TabProps> {
  items: (T | PersistedDefaultTabProps<T>)[];
  selectedIndex?: number;
}

export interface MobileBlotterPanelProps<T extends TabProps> {
  sectionId: LayoutSection;
  layout: LayoutSpec;
  panel: GroupviewPanelState;
  panelConfig: PanelConfigWithTabs<T> | undefined;
  defaultTabs: PersistedDefaultTabProps<T>[];
  /**
   * Renders the body for the active tab. Receives the resolved tab config so
   * the body can wire up the right filter, blotterID, and columns. If omitted,
   * a placeholder is shown.
   */
  renderTab?: (args: MobileBlotterPanelRenderTabArgs<T>) => ReactNode;
  /** Returns an optional count badge for a tab, such as an open-order count. */
  getTabIndicatorCount?: (
    tab: T | PersistedDefaultTabProps<T>,
    tabId: string,
    sourceTabs: (T | PersistedDefaultTabProps<T>)[]
  ) => number | undefined;
  /** Options shown in the sub-tab bar add menu for creating new persisted tabs. */
  addTabOptions?: MobileBlotterPanelAddTabOption<T>[];
  /** Determines whether a tab should be available in the current panel context. */
  isTabIncluded?: (tab: T | PersistedDefaultTabProps<T>) => boolean;
  /** Normalizes or enriches a tab before it is used for navigation and rendering. */
  mapTab?: (tab: T | PersistedDefaultTabProps<T>) => T | PersistedDefaultTabProps<T>;
  /** Content shown when no active tab body can be rendered. */
  fallback?: ReactNode;
}

/**
 * Shared mobile blotter shell: resolves persisted-or-default tabs, redirects to the default tab when the
 * current URL targets the panel root (no `:tabId` segment), renders the SubTabBar for the resolved tabs,
 * and provides panel-param-backed tabs / blotter-table storage (mirrors desktop {@link BlotterPanel}).
 */
export function MobileBlotterPanel<T extends TabProps>({
  sectionId,
  layout,
  panel,
  panelConfig,
  defaultTabs,
  renderTab,
  getTabIndicatorCount,
  addTabOptions,
  isTabIncluded,
  mapTab,
  fallback = null,
}: MobileBlotterPanelProps<T>) {
  const navigate = useNavigate();
  const { tabId } = useParams<{ tabId?: string }>();
  const mixpanel = useMixpanel();
  const { shouldConfirmCloseTab } = useDisplaySettings();
  const { items, selectedTabId } = usePanelTabNavItems<T>({
    sectionId,
    layout,
    panelId: panel.id,
    panelConfig,
    defaultTabs,
    getTabIndicatorCount,
    isTabIncluded,
    mapTab,
  });

  const { tab } = useResolvedTab<T>({ panelConfig, defaultTabs, tabId, isTabIncluded, mapTab });
  const panelApi = useMobileLayoutPanelApi(panel);
  const panelTabs = getTabsStateFromPanelConfig(panelConfig);
  const panelBlottersState = getBlotterTablesStateFromPanelConfig(panelConfig);
  // Keep an optimistic copy so sequential tab actions do not read stale panelConfig before it re-renders.
  const panelTabsRef = useRef<MobileBlotterPanelTabsState<T> | undefined>(panelTabs);
  // SubTabBar receives a stable handler while still calling the latest router navigate function.
  const handleNavigate = useCallbackRef((path: string) => navigate(path));

  const updateTab = useCallback(
    (updatedTab: T | PersistedDefaultTabProps<T>) => {
      const latestPanelTabs = panelTabsRef.current;
      const sourceTabs = getSourceTabs(latestPanelTabs?.items, defaultTabs);
      const selectedIndex =
        latestPanelTabs?.selectedIndex ?? sourceTabs.findIndex(sourceTab => sourceTab.id === (tabId ?? updatedTab.id));
      const nextItems = sourceTabs.map(sourceTab => (sourceTab.id === updatedTab.id ? updatedTab : sourceTab));

      panelApi.updateParameters({
        tabs: {
          items: nextItems,
          selectedIndex,
        },
      });
      panelTabsRef.current = { items: nextItems, selectedIndex };
    },
    [defaultTabs, panelApi, tabId]
  );

  const handleAddTab = useCallback(
    (option: MobileBlotterPanelAddTabOption<T>) => {
      const sourceTabs = getSourceTabs(panelTabsRef.current?.items, defaultTabs);
      const tabDefaults = option.createTab(sourceTabs);
      const newTab = {
        closable: true,
        editable: true,
        reorderable: true,
        ...tabDefaults,
        id: tabDefaults.id ?? uuid(),
      } as T | PersistedDefaultTabProps<T>;
      const nextItems = [...sourceTabs, newTab];

      panelApi.updateParameters({
        tabs: {
          items: nextItems,
          selectedIndex: nextItems.length - 1,
        },
      });
      option.onAdd?.();
      handleNavigate(getMobileLayoutPanelTabRoute(sectionId, layout, panel.id, newTab.id ?? ''));
      panelTabsRef.current = { items: nextItems, selectedIndex: nextItems.length - 1 };
    },
    [defaultTabs, handleNavigate, layout, panel.id, panelApi, sectionId]
  );

  const handleRenameTab = useCallback(
    (item: TabBarItem, label: string) => {
      const sourceTabs = getSourceTabs(panelTabsRef.current?.items, defaultTabs);
      const selectedIndex =
        panelTabsRef.current?.selectedIndex ?? sourceTabs.findIndex(sourceTab => sourceTab.id === tabId);
      const renamedTab = sourceTabs.find(sourceTab => sourceTab.id === item.id);
      const nextItems = sourceTabs.map(sourceTab =>
        sourceTab.id === item.id ? { ...sourceTab, label, usingSmartLabel: false } : sourceTab
      );

      panelApi.updateParameters({
        tabs: {
          items: nextItems,
          selectedIndex,
        },
      });
      panelTabsRef.current = { items: nextItems, selectedIndex };
      mixpanel.track(MixpanelEvent.RenameTab, {
        [MixpanelEventProperty.TabType]: getTabMixpanelTabType(renamedTab, panel.id),
      });
    },
    [defaultTabs, mixpanel, panel.id, panelApi, tabId]
  );

  const handleDeleteTab = useCallback(
    (item: TabBarItem) => {
      const sourceTabs = getSourceTabs(panelTabsRef.current?.items, defaultTabs);
      const deletedIndex = sourceTabs.findIndex(sourceTab => sourceTab.id === item.id);
      const deletedTab = sourceTabs[deletedIndex];
      if (deletedIndex === -1 || deletedTab?.closable !== true) {
        return;
      }

      const nextItems = sourceTabs.filter(sourceTab => sourceTab.id !== item.id);
      const currentUrlSelectedIndex = sourceTabs.findIndex(sourceTab => sourceTab.id === tabId);
      const nextUrlSelectedIndex = nextItems.findIndex(sourceTab => sourceTab.id === tabId);
      const selectedIndex =
        nextUrlSelectedIndex === -1
          ? getClosestIncludedTabIndex(nextItems, deletedIndex, isTabIncluded)
          : nextUrlSelectedIndex;

      panelApi.updateParameters({
        tabs: {
          items: nextItems,
          selectedIndex,
        },
      });
      panelTabsRef.current = { items: nextItems, selectedIndex };
      mixpanel.track(MixpanelEvent.CloseTab, {
        [MixpanelEventProperty.TabType]: getTabMixpanelTabType(deletedTab, panel.id),
      });

      if (currentUrlSelectedIndex === deletedIndex) {
        const selectedTab = nextItems[selectedIndex];
        handleNavigate(
          selectedTab?.id != null
            ? getMobileLayoutPanelTabRoute(sectionId, layout, panel.id, selectedTab.id)
            : getMobileLayoutPanelRoute(sectionId, layout, panel.id)
        );
      }
    },
    [defaultTabs, handleNavigate, isTabIncluded, layout, mixpanel, panel.id, panelApi, sectionId, tabId]
  );

  useEffect(() => {
    panelTabsRef.current = panelTabs;
  }, [panelTabs]);

  const parent: NavigationItem = useMemo(
    () => ({
      id: panel.id,
      label: panel.title,
      children: items,
    }),
    [panel.id, panel.title, items]
  );

  const subTabBarAddOptions = useMemo<SubTabBarAddOption[]>(
    () =>
      addTabOptions?.map(option => ({
        id: option.id,
        label: option.label,
        icon: option.icon,
        onSelect: () => handleAddTab(option),
      })) ?? [],
    [addTabOptions, handleAddTab]
  );

  const renderedTabBody = useMemo(() => {
    if (renderTab && tab && tabId) {
      return renderTab({ tab, tabId, panelConfig, panelId: panel.id, updateTab });
    }

    return fallback;
  }, [fallback, panel.id, panelConfig, renderTab, tab, tabId, updateTab]);

  if (tabId == null && selectedTabId != null) {
    return <Navigate to={getMobileLayoutPanelTabRoute(sectionId, layout, panel.id, selectedTabId)} replace />;
  }

  return (
    <PanelTabsStorageProvider panelApi={panelApi} panelTabs={panelTabs}>
      <PanelBlotterTableStorageProvider panelApi={panelApi} panelBlottersState={panelBlottersState}>
        {renderedTabBody}
        <SubTabBar
          parent={parent}
          onNavigate={handleNavigate}
          addOptions={subTabBarAddOptions}
          onRenameItem={handleRenameTab}
          onDeleteItem={handleDeleteTab}
          shouldConfirmDeleteItem={shouldConfirmCloseTab}
        />
      </PanelBlotterTableStorageProvider>
    </PanelTabsStorageProvider>
  );
}

function getSourceTabs<T extends TabProps>(
  persistedItems: (T | PersistedDefaultTabProps<T>)[] | undefined,
  defaultTabs: PersistedDefaultTabProps<T>[]
) {
  return persistedItems != null && persistedItems.length > 0 ? persistedItems : defaultTabs;
}

function getClosestIncludedTabIndex<T extends TabProps>(
  tabs: (T | PersistedDefaultTabProps<T>)[],
  preferredIndex: number,
  isTabIncluded: ((tab: T | PersistedDefaultTabProps<T>) => boolean) | undefined
) {
  if (tabs.length === 0) {
    return -1;
  }

  const clampedPreferredIndex = Math.min(preferredIndex, tabs.length - 1);
  for (let index = clampedPreferredIndex; index < tabs.length; index++) {
    if (isTabIncluded?.(tabs[index]) !== false) {
      return index;
    }
  }
  for (let index = clampedPreferredIndex - 1; index >= 0; index--) {
    if (isTabIncluded?.(tabs[index]) !== false) {
      return index;
    }
  }

  return -1;
}

function getTabMixpanelTabType<T extends TabProps>(
  tab: T | PersistedDefaultTabProps<T> | undefined,
  fallbackTabType: string
) {
  const tabType = (tab as { tabType?: unknown } | undefined)?.tabType;
  return typeof tabType === 'string' ? tabType : fallbackTabType;
}
